/* eslint-disable react/no-unused-prop-types */

import styled, { createGlobalStyle } from "styled-components";
import isEmpty from "lodash/isEmpty";
import { getMobileBreakpoint } from "./globals";
import { ColorVariables } from "../Colors";

type DefaultTextProps = {
  text?: string | JSX.Element;
  color?: string;
  onClick?: (...args: any) => any;
  bold?: boolean;
  pointer?: boolean;
  maxSize?: number;
};

export const TypographyCSS = createGlobalStyle`
  p,
  a,
  textarea,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0px;
    padding: 0px;
  }
  
  h1 {
    font-style: normal;
    font-weight: 550;
    font-size: 40px;
    line-height: 48px;
  }
  
  h2 {
    font-style: normal;
    font-weight: 550;
    font-size: 36px;
    line-height: 44px;
  }
  
  h3 {
    font-style: normal;
    font-weight: 550;
    font-size: 32px;
    line-height: 40px;
  }
  
  h4 {
    font-style: normal;
    font-weight: 550;
    font-size: 28px;
    line-height: 36px;
  }
  
  p {
    font-style: normal;
    font-weight: 350;
    font-size: 18px;
    line-height: 26px;
  }
  
  .clampText {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
  }
  
  .clampText-2 {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
  
  .all-caps {
    text-transform: uppercase;
  }
  
  .large, .large * {
    font-size: 22px;
    line-height: 32px;
  }
  
  .medium, .medium * {
    font-size: 18px;
    line-height: 26px;
    font-weight: 375;
  }
  
  .small, .small * {
    font-size: 16px;
    line-height: 24px;
    font-weight: 350;
  }
  
  .xsmall, .xsmall * {
    font-size: 14px;
    line-height: 22px;
  }

  strong, .bold, strong, .bold *   {
    font-weight: 550 !important;
  }

  .small.bold, .small.bold * {
    font-weight: 500  !important;
  }

  .large.bold, .large.bold * {
    font-weight: 550 !important;
  }

  .medium.bold, .medium.bold * {
    font-weight: 500  !important;
  }

  .underline{
    text-decoration: underline !important;
  }
  
  /*
    MEDIA QUERIES
  */
  
  @media (max-width: ${getMobileBreakpoint}) { 
    h1 {
      font-size: 40px;
      line-height: 44px;
    }
  
    h2 {
      line-height: 40px;
    }
  
    h3 {
      font-size: 28px;
      line-height: 36px;
    }
  
    h4 {
      font-size: 24px;
      line-height: 32px;
    }
  
    p {
      font-size: 16px;
      line-height: 24px;
    }
  
    .large, .large * {
      font-size: 20px;
      line-height: 28px;
    }
  
    .medium, .medium * {
      font-size: 16px;
      line-height: 24px;
    }
  
    .small, .small * {
      font-size: 14px;
      line-height: 22px;
    }
  
    .xsmall, .xsmall * {
      font-size: 12px;
      line-height: 18px;
      font-weight: 350;
    }
  }
`;

export const NumberedList = styled.ol`
  list-style: none;
  counter-reset: counter;
  padding-left: 0;
  li {
    padding-left: 0;
    counter-increment: counter;
    margin-bottom: 24px;
    display: flex;
    align-items: center;
  }
  li::before {
    flex: none;
    align-self: baseline;
    content: counter(counter);
    border-radius: 50%;
    background: ${ColorVariables.mint};
    color: white;
    height: 35px;
    width: 35px;
    font-size: 20px;
    font-family: inherit;
    font-weight: 500;
    text-align: center;
    line-height: 35px;
    display: block;
    margin-right: 10px;
  }
`;

export const StyledHeaderOne = styled.div<DefaultTextProps>`
  p {
    text-shadow: none;
    font-style: normal;
    font-weight: bold;
    font-size: 36px;
    line-height: 46px;
    color: ${(props) => props.color};

    @media (max-width: ${getMobileBreakpoint}) {
      font-size: 26px;
      line-height: 36px;
    }
  }
`;

export const StyledHeaderTwo = styled.div<DefaultTextProps>`
  p {
    font-style: normal;
    font-weight: ${(props) => (props.bold ? 700 : 600)};
    font-size: 22px;
    line-height: 32px;
    color: ${(props) => props.color};
    cursor: ${(props) => (props.pointer ? "pointer" : "default")};

    &:hover {
      opacity: ${(props) => (props.pointer ? "0.8" : "1")};
    }

    @media (max-width: ${getMobileBreakpoint}) {
      font-size: 20px;
      line-height: 30px;
    }
  }
`;

export const StyledDescription = styled.div<DefaultTextProps>`
  p {
    font-style: normal;
    font-weight: ${(props) => (props.bold ? 700 : "normal")};
    font-size: 17px;
    line-height: 28px;
    color: ${(props) => props.color};
    cursor: ${(props) => (props.pointer ? "pointer" : "default")};

    @media (max-width: ${getMobileBreakpoint}) {
      font-size: 14px;
      line-height: 22px;
    }
  }
`;

export const StyledNote = styled.div<DefaultTextProps>`
  font-style: normal;
  font-weight: ${(props) => (props.bold ? "600" : "normal")};
  font-size: 14px;
  line-height: 20px;
  color: ${(props) => props.color};
  cursor: ${(props) => (props.pointer ? "pointer" : "default")};
  ${(props) =>
    props.bold
      ? `
    * {
      font-weight: 600;
    }
  `
      : undefined};

  @media (max-width: ${getMobileBreakpoint}) {
    font-size: 12px;
    line-height: 16px;
  }
`;

export const StyledFinePrint = styled.div<DefaultTextProps>`
  font-style: normal;
  font-size: 12px;
  line-height: 16px;
  color: ${(props) => props.color};
  font-weight: ${(props) => (props.bold ? 600 : "normal")};
  cursor: ${(props) => (props.pointer ? "pointer" : "default")};

  @media (max-width: ${getMobileBreakpoint}) {
    font-size: 10px;
    line-height: 14px;
  }
`;

export const StyledButtonText = styled.div`
  p {
    font-style: normal;
    font-weight: 600;
    font-size: 20px;
    line-height: 0px;
    color: ${(props) => props.color};

    @media (max-width: ${getMobileBreakpoint}) {
      font-size: 15px;
    }
  }
`;

export const StyledHeader = styled.p`
  font-style: normal;
  font-weight: normal;
  font-size: 20px;
  line-height: 30px;
  color: ${(props) => props.color};
`;

const getText = (props: { maxSize?: number; text?: string | JSX.Element }) => {
  const { maxSize, text } = props;
  if (maxSize && typeof text === "string" && !isEmpty(text)) {
    if (text.length > maxSize) {
      return `${text.slice(0, maxSize - 8)}...`;
    }
    return text.slice(0, maxSize - 1);
  }
  return text;
};

export const HeaderOne = ({ text = "", color = "black" }: DefaultTextProps) => {
  return <StyledHeaderOne color={color}>{text}</StyledHeaderOne>;
};

export const HeaderTwo = ({ text = "", bold, color = "black", onClick, pointer = false }: DefaultTextProps) => {
  return (
    <StyledHeaderTwo pointer={pointer} onClick={onClick} bold={bold} color={color}>
      {text}
    </StyledHeaderTwo>
  );
};

export const Description = (props: DefaultTextProps) => {
  const { color = "black", onClick, text = "", maxSize, bold = false, pointer = false } = props;
  return (
    <StyledDescription onClick={onClick} color={color} bold={bold} pointer={pointer}>
      <p>
        {getText({
          text,
          maxSize,
        })}
      </p>
    </StyledDescription>
  );
};

export const Note = (props: DefaultTextProps) => {
  const { color = "black", text = "", maxSize, bold = false, onClick, pointer = false } = props;
  return (
    <StyledNote pointer={pointer} onClick={onClick} bold={bold} color={color}>
      <p>
        {getText({
          text,
          maxSize,
        })}
      </p>
    </StyledNote>
  );
};

export default TypographyCSS;
